import React, { FC, useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import { PageIndex } from 'components/UI/PageIndex';
import { Row } from 'components/UI/Row';
import { BottomActionBar } from 'components/UI/BottomActionBar';
import { ButtonLoader } from 'components/Loaders/ButtonLoader';
import { Search } from 'components/Search/Search';
import { axiosInstance } from 'features/axiosInstance';
import { documentsPrefix, DocLinkType } from 'routes/routes';
import { linkToResource, getDocumentLinkLoaders } from './documentsLink';

interface ProductsLinkProps {
  documentId: string;
  linkType: string;
  searchLabelKey?: string;
}

type Resource = {
  created_at: string;
  description: string;
  link_id: string;
  link_parent_id: string;
  link_parent_type: string;
  link_type: string;
  linked: boolean;
  name: string;
  resource_code: string;
};

type Pagination = {
  index: number;
  indexesCount?: number;
  searchTerm?: string;
  size?: number;
};

const ResourceList: FC<ProductsLinkProps> = ({ documentId, linkType, searchLabelKey }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const tableRef = useRef<HTMLTableElement>(null);
  const { isSaving } = useSelector(getDocumentLinkLoaders);
  const [isLoading, setIsLoading] = useState(true);
  const [resourcesList, setResourcesList] = useState<Resource[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [unlink, setUnlink] = useState<string[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    index: 1
  });

  const fetchResources = useCallback(
    async (page: number, searchTerm?: string) => {
      setIsLoading(true);

      /* `${documentsPrefix}/${documentId}/resources/${linkType} -> prefix/id/resources/type */

      const { data } = await axiosInstance.get(`${documentsPrefix}/${documentId}/resources/${linkType}`, {
        params: {
          index: page,
          size: 30,
          search: searchTerm,
          orderBy: linkType === DocLinkType.SERIAL_NUMBER ? 'date' : 'name',
          orderDir: linkType === DocLinkType.SERIAL_NUMBER ? 'desc' : 'asc'
        }
      });
      const { resources, index, indexes_count: indexesCount, size } = data;
      setResourcesList(resources);
      setPagination({ index, indexesCount, size, searchTerm });
      const alreadySelected = resources
        .filter((r: Resource) => r.linked && !selected.includes(r.link_id))
        .map((r: Resource) => r.link_id);
      setSelected([...selected, ...alreadySelected]);
      if (tableRef) {
        tableRef?.current?.scrollIntoView();
        window.scrollTo({ top: 0 });
      }

      setIsLoading(false);
    },
    [documentId, linkType, selected]
  );

  useEffect(() => {
    fetchResources(1);
  }, []);

  const isSerialNumber = useCallback(() => linkType === DocLinkType.SERIAL_NUMBER, [linkType]);
  const isProduct = useCallback(() => linkType === DocLinkType.PRODUCT, [linkType]);

  return (
    <>
      <div>
        <Search
          resetFunction={() => fetchResources(1)}
          searchFunction={(st: string) => fetchResources(1, st)}
          searchLabel={searchLabelKey || linkType}
        />
      </div>
      <TableContainer component={Paper} elevation={3} style={{ margin: '25px 0 90px', maxHeight: '60vh' }}>
        <Table stickyHeader aria-label="order list" ref={tableRef}>
          <TableHead>
            <TableRow>
              {isProduct() && <TableCell style={{ width: '15%' }}>{t(`documentsLink.resource code`)}</TableCell>}
              <TableCell>{t(`documentsLink.${linkType}`)}</TableCell>
              {isSerialNumber() && <TableCell>{t(`documentsLink.resource code`)}</TableCell>}
              <TableCell>{isSerialNumber() ? t(`documentsLink.product`) : t(`documentsLink.description`)}</TableCell>
              <TableCell>{t(`documentsLink.created at`)}</TableCell>
              <TableCell style={{ width: '30px' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell>
                  <Row height="50vh" justifyContent="center" alignItems="center" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && isEmpty(resourcesList) && (
              <Row height="50vh" justifyContent="center" alignItems="center">
                {t('documentsLink.no resources found', { resource: linkType.toLowerCase().replace(/_/g, ' ') })}
              </Row>
            )}
            {resourcesList.map((p: Resource) => (
              <TableRow
                key={`product_${p.link_id}`}
                hover
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const isChecked = selected.includes(p.link_id);
                  setSelected(!isChecked ? [...selected, p.link_id] : filter(selected, s => s !== p.link_id));
                  setUnlink(isChecked ? [...unlink, p.link_id] : filter(unlink, s => s !== p.link_id));
                }}
              >
                {isProduct() && <TableCell style={{ width: '15%' }}>{p.resource_code}</TableCell>}
                <TableCell>{p.name}</TableCell>
                {isSerialNumber() && (
                  <>
                    <TableCell>{p.resource_code}</TableCell>
                  </>
                )}
                <TableCell>{p.description}</TableCell>
                <TableCell>{moment(p?.created_at).format('YYYY-MM-DD hh:mm:ss')}</TableCell>
                <TableCell style={{ width: '30px' }} align="center">
                  <Checkbox
                    checked={selected.includes(p.link_id)}
                    onChange={(_, value) => {
                      setSelected(value ? [...selected, p.link_id] : filter(selected, s => s !== p.link_id));
                      setUnlink(!value ? [...unlink, p.link_id] : filter(unlink, s => s !== p.link_id));
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <BottomActionBar>
        <Row padding="20px" justifyContent="center">
          <PageIndex
            justifyContent="center"
            index={pagination.index}
            indexesCount={pagination.indexesCount || 0}
            onChange={(_, page: number) => fetchResources(page, pagination.searchTerm)}
          />
          <Button
            color="primary"
            disabled={isLoading || isSaving}
            variant="contained"
            style={{
              position: 'absolute',
              right: '40px'
            }}
            onClick={() => {
              dispatch(
                linkToResource({
                  added: selected,
                  documentId,
                  linkType,
                  removed: unlink
                })
              );
            }}
          >
            {t('label.save')}
            <ButtonLoader show={isSaving} />
          </Button>
        </Row>
      </BottomActionBar>
    </>
  );
};

export { ResourceList };
